import {
  REFERRER_DATA_CORRECT_MESSAGE,
  REFERRER_PROSPECT_CHANNEL_MESSAGE,
  REFERRER_PROSPECT_CONSENT_MESSAGE,
  referrerIntakeReferrerSchema,
  type ReferrerIntakeSubmitBody,
} from '@brimo/shared';
import {
  buildAssets,
  buildExpenses,
  buildIncome,
  buildPerson,
  type FieldErrors,
  type FormState,
} from '../public/form';

/**
 * Local state of the referrer intake `/t/:token` (D-248): the customer form's four data steps (same form state, same
 * builders and messages as `/sd/:token`, no autosave) plus step 5 „Tippgeber\" — the referrer's own data and the two
 * confirmations. The whole submission goes out in one request.
 */

export type IntakeStepNo = 1 | 2 | 3 | 4 | 5;

export interface ReferrerForm {
  name: string;
  email: string;
  phone: string;
  note: string;
  dataCorrect: boolean;
  prospectConsented: boolean;
}

export interface IntakeForm extends FormState {
  referrer: ReferrerForm;
}

/** A fresh form; the referrer's own contact data can be carried over into the next submission. */
export function emptyIntakeForm(referrer?: Pick<ReferrerForm, 'name' | 'email' | 'phone'>): IntakeForm {
  return {
    person: {
      firstName: '',
      lastName: '',
      bornOn: '',
      maritalStatus: '',
      children: '',
      nationality: '',
      street: '',
      postalCode: '',
      city: '',
      phone: '',
      email: '',
    },
    income: {
      employment: 'employed',
      employer: '',
      employedSince: '',
      contract: '',
      fixedTermUntil: '',
      netIncome: '',
      salariesPerYear: '',
      specialPayments: '',
      otherIncome: '',
      otherIncomeType: '',
      rentalIncome: '',
    },
    expenses: {
      housingSituation: 'rent',
      grossRent: '',
      rentContinues: 'yes',
      loans: '',
      loansRemainingTerm: '',
      alimony: '',
      privateHealthInsurance: '',
    },
    assets: { equity: '', securities: '', other: '' },
    referrer: {
      name: referrer?.name ?? '',
      email: referrer?.email ?? '',
      phone: referrer?.phone ?? '',
      note: '',
      dataCorrect: false,
      prospectConsented: false,
    },
  };
}

/** Field errors of one step (keys as the step components read them: `firstName`, `netIncomeCents`, `email` …). */
export function validateStep(step: IntakeStepNo, form: IntakeForm): FieldErrors {
  switch (step) {
    case 1: {
      const built = buildPerson(form.person);
      const errors = { ...built.errors };
      // the prospect must be reachable: the contact is matched by e-mail or phone
      if (
        form.person.email.trim() === '' &&
        form.person.phone.trim() === '' &&
        errors['email'] === undefined
      ) {
        errors['email'] = REFERRER_PROSPECT_CHANNEL_MESSAGE;
      }
      return errors;
    }
    case 2:
      return buildIncome(form.income).errors;
    case 3:
      return buildExpenses(form.expenses).errors;
    case 4:
      return buildAssets(form.assets).errors;
    case 5:
      return validateReferrer(form.referrer);
  }
}

function validateReferrer(referrer: ReferrerForm): FieldErrors {
  const errors: FieldErrors = {};
  const parsed = referrerIntakeReferrerSchema.safeParse(referrerBody(referrer));
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const key = String(issue.path[0] ?? '');
      if (key && errors[key] === undefined) errors[key] = issue.message;
    }
  }
  if (!referrer.dataCorrect) errors['dataCorrect'] = REFERRER_DATA_CORRECT_MESSAGE;
  if (!referrer.prospectConsented) errors['prospectConsented'] = REFERRER_PROSPECT_CONSENT_MESSAGE;
  return errors;
}

function referrerBody(referrer: ReferrerForm): ReferrerIntakeSubmitBody['referrer'] {
  const phone = referrer.phone.trim();
  const note = referrer.note.trim();
  return {
    name: referrer.name.trim(),
    email: referrer.email.trim(),
    ...(phone === '' ? {} : { phone }),
    ...(note === '' ? {} : { note }),
  };
}

/** The request body, or null while any step is invalid. */
export function buildSubmitBody(form: IntakeForm): ReferrerIntakeSubmitBody | null {
  const person = buildPerson(form.person).body;
  const income = buildIncome(form.income).body;
  const expenses = buildExpenses(form.expenses).body;
  const assets = buildAssets(form.assets).body;
  if (person === null || income === null || expenses === null || assets === null) return null;
  if (!form.referrer.dataCorrect || !form.referrer.prospectConsented) return null;
  return {
    person,
    income,
    expenses,
    assets,
    referrer: referrerBody(form.referrer),
    consents: { dataCorrect: true, prospectConsented: true },
  };
}

const stepOfPrefix: Record<string, IntakeStepNo> = {
  person: 1,
  income: 2,
  expenses: 3,
  assets: 4,
  referrer: 5,
  consents: 5,
};

/**
 * Server field errors (`person.firstName`, `income.netIncomeCents`, `referrer.email`, `consents.dataCorrect`) grouped
 * by step with the keys of the step components. Paths the form does not know stay out (the caller shows a banner).
 */
export function errorsByStep(fields: Record<string, string>): Map<IntakeStepNo, FieldErrors> {
  const result = new Map<IntakeStepNo, FieldErrors>();
  for (const [path, message] of Object.entries(fields)) {
    const [prefix = '', field = ''] = path.split('.');
    const step = stepOfPrefix[prefix];
    if (step === undefined || field === '') continue;
    const errors = result.get(step) ?? {};
    if (errors[field] === undefined) errors[field] = message;
    result.set(step, errors);
  }
  return result;
}
